import React, { useContext, useCallback } from 'react';
import { AppContext } from '../context/AppContext';
import SearchModal from './SearchModal';

/**
 * Contact search modal with lazy loading
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {function} onSelect - Callback when a contact is selected
 * @param {number} selectedId - Currently selected contact ID (for highlighting)
 * @param {array} excludeIds - Array of contact IDs to exclude from results
 */
const ContactSearchModal = ({
  open,
  onClose,
  onSelect,
  selectedId = null,
  excludeIds = []
}) => {
  const { token } = useContext(AppContext);

  const fetchContacts = useCallback(async (searchTerm, page, limit) => {
    try {
      const response = await fetch('/api/contacts?all=true', {
        headers: token ? { Authorization: token } : {}
      });

      if (!response.ok) {
        throw new Error('Failed to fetch contacts');
      }

      let contacts = await response.json();

      // Filter out excluded contacts
      if (excludeIds.length > 0) {
        contacts = contacts.filter(c => !excludeIds.includes(c.id));
      }

      // Client-side search filtering
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        contacts = contacts.filter(contact => {
          const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.toLowerCase();
          const email = (contact.emailAddress || '').toLowerCase();
          const phone = (contact.phoneNumber || '').toLowerCase();
          return fullName.includes(term) || email.includes(term) || phone.includes(term);
        });
      }

      // Sort by last name, first name
      contacts.sort((a, b) => {
        const lastA = (a.lastName || '').toLowerCase();
        const lastB = (b.lastName || '').toLowerCase();
        if (lastA !== lastB) return lastA.localeCompare(lastB);
        const firstA = (a.firstName || '').toLowerCase();
        const firstB = (b.firstName || '').toLowerCase();
        return firstA.localeCompare(firstB);
      });

      // Pagination
      const start = (page - 1) * limit;
      const paginatedContacts = contacts.slice(start, start + limit);

      return {
        items: paginatedContacts,
        total: contacts.length
      };
    } catch (error) {
      console.error('Error fetching contacts:', error);
      return { items: [], total: 0 };
    }
  }, [token, excludeIds]);

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (contact) => (
        <div>
          <div className="font-medium text-gray-900">
            {contact.firstName} {contact.lastName}
          </div>
          {contact.status?.name && (
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 mt-1">
              {contact.status.name}
            </span>
          )}
        </div>
      )
    },
    {
      key: 'email',
      label: 'Email',
      render: (contact) => (
        <span className="text-gray-600">{contact.emailAddress || '-'}</span>
      )
    },
    {
      key: 'phone',
      label: 'Phone',
      render: (contact) => (
        <span className="text-gray-600">{contact.phoneNumber || '-'}</span>
      )
    }
  ];

  return (
    <SearchModal
      open={open}
      onClose={onClose}
      onSelect={onSelect}
      title="Select Contact"
      searchPlaceholder="Search by name, email, or phone..."
      fetchItems={fetchContacts}
      columns={columns}
      getItemId={(contact) => contact.id}
      emptyMessage="No contacts found"
      selectedId={selectedId}
    />
  );
};

export default ContactSearchModal;
